// Voice config types + ElevenLabs presets. Ported from QuickLifts-Web's
// /admin/ai-voice page so the Parrot admin can speak the same protocol
// (provider, voiceId, presetId, settings, punctuationPauses). Kept lean
// — we only support ElevenLabs here since Parrot's WOTD pronunciation
// is the sole consumer. If we ever need OpenAI TTS as a fallback we
// can add the OPENAI_VOICES list back.

export type ElevenLabsVoiceSettings = {
  stability: number;
  similarityBoost: number;
  style: number;
  useSpeakerBoost: boolean;
  speed: number;
};

export type WotdVoiceConfig = {
  // Locked to elevenlabs for now; kept as a discriminant so swapping
  // providers later doesn't require a schema migration.
  provider: 'elevenlabs';
  voiceId: string;
  presetId: string;
  elevenLabsSettings: ElevenLabsVoiceSettings | null;
  punctuationPauses: boolean;
  updatedAt: number;
};

export type VoiceChoice = {
  id: string;
  label: string;
  description?: string;
};

export type ElevenLabsPreset = {
  id: string;
  label: string;
  description: string;
  settings?: ElevenLabsVoiceSettings;
  useVoiceDefaults?: boolean;
};

// Curated ElevenLabs voice IDs. Same IDs the Pulse admin uses — these
// are stable ElevenLabs catalog voices, not custom-trained models.
// Sarah is the default for Parrot: clean enunciation, neutral US accent,
// matches a friendly-tutor read of single-word vocabulary.
export const ELEVENLABS_VOICES: VoiceChoice[] = [
  { id: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah', description: 'Soft, friendly US — recommended for WOTD' },
  { id: '21m00Tcm4TlvDq8ikWAM', label: 'Rachel', description: 'Calm, classic narrator' },
  { id: 'EST9Ui6982FZPSi7gCHi', label: 'Elise', description: 'Warm, conversational' },
  { id: 'gJx1vCzNCD1EQHT212Ls', label: 'Ava', description: 'Bright, modern US' },
  { id: 'MF3mGyEYCl7XYWbV9V6O', label: 'Elli', description: 'Young, energetic' },
  { id: 'XB0fDUnXU5powFXDhCwa', label: 'Charlotte', description: 'British, refined' },
  { id: 'piTKgcLEGmPE4e6mEKli', label: 'Nicole', description: 'Whispery, intimate' },
];

export const DEFAULT_VOICE_ID = ELEVENLABS_VOICES[0].id;

export const ELEVENLABS_PRESETS: ElevenLabsPreset[] = [
  {
    id: 'default',
    label: 'Default',
    description: 'Use the voice author’s native ElevenLabs defaults — no override.',
    useVoiceDefaults: true,
  },
  {
    id: 'grounded',
    label: 'Grounded',
    description: 'Stable and calm with light variation. Best for adult-learner cadence.',
    settings: {
      stability: 0.52,
      similarityBoost: 0.78,
      style: 0.28,
      useSpeakerBoost: true,
      speed: 0.96,
    },
  },
  {
    id: 'expressive',
    label: 'Expressive',
    description: 'Natural emphasis without sounding theatrical. Default for WOTD.',
    settings: {
      stability: 0.34,
      similarityBoost: 0.82,
      style: 0.65,
      useSpeakerBoost: true,
      speed: 0.94,
    },
  },
  {
    id: 'animated',
    label: 'Animated',
    description: 'Stronger inflection. Useful for younger users or playful copy.',
    settings: {
      stability: 0.24,
      similarityBoost: 0.8,
      style: 0.82,
      useSpeakerBoost: true,
      speed: 0.98,
    },
  },
];

export const DEFAULT_PRESET_ID = 'expressive';

const clamp = (value: unknown, min = 0, max = 1, fallback = 0) => {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(max, Math.max(min, numeric));
};

export function getElevenLabsPreset(presetId?: string | null): ElevenLabsPreset {
  return (
    ELEVENLABS_PRESETS.find((preset) => preset.id === presetId) ||
    ELEVENLABS_PRESETS.find((preset) => preset.id === DEFAULT_PRESET_ID) ||
    ELEVENLABS_PRESETS[0]
  );
}

export function shouldUseVoiceDefaults(presetId?: string | null): boolean {
  return Boolean(getElevenLabsPreset(presetId).useVoiceDefaults);
}

export function normalizeElevenLabsSettings(
  settings?: Partial<ElevenLabsVoiceSettings> | null,
  presetId?: string | null
): ElevenLabsVoiceSettings {
  const fallback =
    getElevenLabsPreset(presetId).settings ||
    getElevenLabsPreset(DEFAULT_PRESET_ID).settings || {
      stability: 0.34,
      similarityBoost: 0.82,
      style: 0.65,
      useSpeakerBoost: true,
      speed: 0.94,
    };

  return {
    stability: clamp(settings?.stability, 0, 1, fallback.stability),
    similarityBoost: clamp(settings?.similarityBoost, 0, 1, fallback.similarityBoost),
    style: clamp(settings?.style, 0, 1, fallback.style),
    useSpeakerBoost:
      typeof settings?.useSpeakerBoost === 'boolean'
        ? settings.useSpeakerBoost
        : fallback.useSpeakerBoost,
    speed: clamp(settings?.speed, 0.7, 1.2, fallback.speed),
  };
}

export function normalizeWotdVoiceConfig(raw?: Partial<WotdVoiceConfig> | null): WotdVoiceConfig {
  const presetId =
    typeof raw?.presetId === 'string' && raw.presetId.trim() ? raw.presetId : DEFAULT_PRESET_ID;
  const voiceId =
    typeof raw?.voiceId === 'string' && raw.voiceId.trim() ? raw.voiceId : DEFAULT_VOICE_ID;
  return {
    provider: 'elevenlabs',
    voiceId,
    presetId,
    elevenLabsSettings: shouldUseVoiceDefaults(presetId)
      ? null
      : normalizeElevenLabsSettings(raw?.elevenLabsSettings || undefined, presetId),
    punctuationPauses:
      typeof raw?.punctuationPauses === 'boolean' ? raw.punctuationPauses : false,
    updatedAt: typeof raw?.updatedAt === 'number' ? raw.updatedAt : Date.now(),
  };
}
